import _ from 'lodash';
import React, { useState, useEffect } from 'react';
import { makeStyles } from 'design-system/styles';
import { Caption } from 'design-system';
import InputText from 'design-system/InputText';
import { TextButton } from 'design-system/Button';
import { useAsyncFn, useToggle } from 'react-use-v13';
import { assertDefined } from '@scale/shared/types/utils';
import classNames from 'classnames';
import queryString from 'query-string';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import useUpdateEffect from 'react-use-v13/lib/useUpdateEffect';
import apiFetch from '@scale/frontend-lib/api-fetch';
import Separator from 'react-scale/components/design-system/Separator';
import {
  SpeedAuditResult,
  SubtaskAssignment,
} from 'react-scale/components/audits/SpeedAudit/types';
import { BenchmarkThresholdAuditMetadata } from '@scale/shared/types/corp/attemptAudit';
import { IWorkerIdToEmailMap } from '@scale/shared/types/corp/quality';
import { IInstructionItemJson } from '@scale/shared/types/instructionitem';
import { IIMetricsData } from 'react-scale/components/audits/SpeedAudit/Gatekeeper/types';
import QualityLabModal from 'react-scale/components/audits/SpeedAudit/Gatekeeper/QualityLabModal';
import ReviewerExclusionModal from 'react-scale/components/audits/SpeedAudit/Gatekeeper/ReviewerExclusionModal';
import { RULE_WEIGHTS } from 'react-scale/components/audits/SpeedAudit/Gatekeeper/utils';
interface IBenchmarkThresholdAuditSidebarProps {
  auditBatchMetadata?: BenchmarkThresholdAuditMetadata;
  batchIdToUpdate: string;
  projectId: string;
  projectInstrItems: IInstructionItemJson[];
  results: SpeedAuditResult[];
  onUpdateAuditBatch: (auditBatch: any, attemptIds: string[]) => Promise<void>;
  workerIdToEmailMap: IWorkerIdToEmailMap;
  currentResponse?: SubtaskAssignment;
}

const BenchmarkThresholdAuditSidebar = (props: IBenchmarkThresholdAuditSidebarProps) => {
  const {
    batchIdToUpdate,
    projectId,
    projectInstrItems,
    results,
    onUpdateAuditBatch,
    auditBatchMetadata: {
      desiredQuality = 0,
      instrItemThresholds = {},
      reviewLevels = [],
      subtaskBatches = [],
      manuallyExcludedReviewers = [],
      minimumBenchmarksPerII = {},
      tasksFound = 0,
      windowStartingAt,
    } = {},
    workerIdToEmailMap,
    currentResponse,
  } = props;

  const [showingQualityLabModal, setShowingQualityLabModal] = useState(false);
  const [showingReviewerExclusionModal, setShowingReviewerExclusionModal] = useState(false);
  const [loadingAuditData, setLoadingAuditData] = useState(false);
  const [addingExcludedReviewer, setAddingExcludedReviewer] = useState(false);
  const [removingExcludedReviewer, setRemovingExcludedReviewer] = useState('');
  const [metricsData, fetchMetricsData] = useAsyncFn(async () => {
    assertDefined(windowStartingAt);
    const params = {
      windowStartingAt,
      reviewLevels,
      subtaskBatches,
    };
    assertDefined(projectId);
    return apiFetch<IIMetricsData>(
      null,
      `corp-api/quality/${projectId}/ii_metrics?${queryString.stringify(params)}`,
      {
        method: 'GET',
        maxRetries: 1,
      },
    );
  }, [projectId, windowStartingAt]);

  useEffect(() => {
    fetchMetricsData();
  }, [projectId]);

  useUpdateEffect(() => {
    if (loadingAuditData && !metricsData.loading && metricsData.value) {
      setLoadingAuditData(false);
    }
  }, [loadingAuditData, metricsData]);

  // Function to count number of tasks corresponding to the worker, that also pass ii thresholds
  // This adapts the logic built on the backend for calculating # of tasks in a batch,
  //   to finding the # of tasks that a specific reviewer contributes to a batch.
  //   It aggregates all unique tasks among threshold-passing reviewers, then counts
  //   how many tasks would be excluded if the reviewer was excluded.
  const countReviewerValidAttempts = (reviewerID: string, metrics: IIMetricsData) => {
    // TODO: Calculate passingWorkers & allPassingAttempts only once, on metrics load
    const passingWorkers: string[] = [];
    _.forEach(metrics.filteredMetricsByWorker, (reviewerMetrics, workerID) => {
      const counts = metrics.filteredCountsByWorker?.[workerID];
      const passingThresholds = _.every(
        instrItemThresholds,
        (threshold, ii) =>
          reviewerMetrics[ii] >= (threshold[0] || 0) &&
          reviewerMetrics[ii] <= (threshold[1] || 1) &&
          (counts && counts[ii] && minimumBenchmarksPerII?.[ii]
            ? counts[ii] >= minimumBenchmarksPerII[ii]
            : true),
      );
      if (passingThresholds) {
        passingWorkers.push(workerID);
      }
    });
    if (!_.includes(passingWorkers, reviewerID)) {
      return 0;
    }

    const allPassingAttempts = _.uniqBy(
      _.flatMap(passingWorkers, tasker => metrics.attemptsByWorker[tasker]),
      attempt => attempt.task.toString(),
    );
    const allPassingAttemptsExceptReviewer = _.uniqBy(
      _.flatMap(
        _.filter(passingWorkers, id => id !== reviewerID),
        tasker => metrics.attemptsByWorker[tasker],
      ),
      attempt => attempt.task.toString(),
    );

    return allPassingAttempts.length - allPassingAttemptsExceptReviewer.length;
  };

  const classes = useStyles();
  const [collapsed, toggleCollapsed] = useToggle(false);

  const totalScore = _.sumBy(results, r => (r.result ? RULE_WEIGHTS[r.result] : 0));
  const totalPossibleScore = _.sumBy(results, r => (r.result ? 1 : 0));
  const minPotentialScore = totalScore / results.length;
  const maxPotentialScore = (results.length - (totalPossibleScore - totalScore)) / results.length;

  const numCompletedAudits = _.filter(results, r => r.result).length;

  let rating;
  if (minPotentialScore > desiredQuality) {
    rating = 'Good';
  } else if (maxPotentialScore < desiredQuality) {
    rating = 'Poor';
  } else {
    rating = 'Unknown';
  }

  // TODO: In SpeedAudit, retrieve & pass subtask.attempters before normalize is called on the lookupObject
  /*
    TypeScript complains here about retrieving .attempters, because
    normalizeAssignment should technically filter out .attempters
    after the attemptAudit lookup in SpeedAudit.tsx.
  */
  // @ts-ignore
  const curReviewerID: string = _.last(currentResponse?.subtask?.attempters || []) || '';
  const prepopulatedReviewerId = workerIdToEmailMap?.[curReviewerID]?.email || curReviewerID;

  return (
    <div className={classNames(classes.nav, { collapsed })}>
      {metricsData.value && (
        <QualityLabModal
          defaultInstrItemIds={instrItemThresholds}
          defaultReviewLevels={reviewLevels}
          batchIdToUpdate={batchIdToUpdate}
          manuallyExcludedReviewers={manuallyExcludedReviewers}
          projectId={projectId}
          metricsData={metricsData.value}
          instructionItems={projectInstrItems}
          currentMinimumBenchmarksPerII={minimumBenchmarksPerII}
          isOpen={!loadingAuditData && showingQualityLabModal}
          onClose={() => {
            setShowingQualityLabModal(false);
          }}
          onRecalculateMetrics={_.noop}
          usedLookbackDays={0}
          onUpdateAuditBatch={onUpdateAuditBatch}
        />
      )}
      {metricsData.value && (
        <ReviewerExclusionModal
          instrItemThresholds={instrItemThresholds}
          batchIdToUpdate={batchIdToUpdate}
          manuallyExcludedReviewers={manuallyExcludedReviewers}
          addingExcludedReviewer={addingExcludedReviewer}
          removingExcludedReviewer={removingExcludedReviewer}
          workerIdToEmailMap={workerIdToEmailMap}
          minimumBenchmarksPerII={minimumBenchmarksPerII}
          metricsData={metricsData.value}
          countReviewerValidAttempts={countReviewerValidAttempts}
          isOpen={!loadingAuditData && showingReviewerExclusionModal}
          onClose={() => {
            setShowingReviewerExclusionModal(false);
            setAddingExcludedReviewer(false);
            setRemovingExcludedReviewer('');
          }}
          onUpdateAuditBatch={onUpdateAuditBatch}
          prepopulatedReviewerId={prepopulatedReviewerId}
        />
      )}
      <span className="nav-icon" onClick={toggleCollapsed}>
        <FontAwesomeIcon icon="caret-left" />
      </span>
      <Caption mono medium>
        Estimated Quality
      </Caption>
      <h3 className={classNames(classes.qualitySection, rating.toLowerCase())}>{rating}</h3>
      <Caption mono>
        We think quality is somewhere between {formatPct(totalScore, results.length)} and{' '}
        {formatPct(results.length - (totalPossibleScore - totalScore), results.length)}
      </Caption>
      {rating === 'Unknown' && (
        <div>
          <Caption mono>This estimate will improve as you complete more audits</Caption>
        </div>
      )}
      <Separator />
      <div style={{ margin: '6px 0' }}>
        <Caption mono>Audits Finished</Caption>
        <span style={{ marginLeft: 8, opacity: 0.6 }}>
          <Caption mono>{numCompletedAudits}</Caption>
        </span>
      </div>
      <div style={{ margin: '6px 0' }}>
        <Caption mono>Audits Remaining</Caption>
        <span style={{ marginLeft: 8, opacity: 0.6 }}>
          <Caption mono>{results.length - numCompletedAudits}</Caption>
        </span>
      </div>
      <div style={{ margin: '6px 0' }}>
        <Caption mono>Batch Info</Caption>
        <span style={{ marginLeft: 8, opacity: 0.6 }}>
          <Caption mono>
            {tasksFound} Tasks ({reviewLevels.map(r => `L${r}`).join(',')})
          </Caption>
        </span>
      </div>
      <Separator />
      <div style={{ margin: '6px 0' }}>
        <Caption mono>Manually Excluded Reviewers</Caption>
        {_.map(manuallyExcludedReviewers, (reviewerID: string) => {
          let attemptsCountText = '';
          if (!metricsData.loading && metricsData.value) {
            const attemptsCount = countReviewerValidAttempts(reviewerID, metricsData.value);
            attemptsCountText = ` (${attemptsCount} tasks)`;
          }
          return (
            <div key={reviewerID}>
              <Caption medium>
                {workerIdToEmailMap[reviewerID]?.email ?? reviewerID}
                {attemptsCountText}
              </Caption>
              <span
                onClick={() => {
                  setLoadingAuditData(true);
                  setRemovingExcludedReviewer(reviewerID);
                  setShowingReviewerExclusionModal(true);
                }}
              >
                <FontAwesomeIcon icon="trash" style={{ float: 'right' }} />
              </span>
            </div>
          );
        })}
        <TextButton
          type="primary"
          onClick={() => {
            setAddingExcludedReviewer(true);
            setLoadingAuditData(true);
            setShowingReviewerExclusionModal(true);
          }}
          disabled={loadingAuditData || showingQualityLabModal || showingReviewerExclusionModal}
        >
          Exclude Reviewer
        </TextButton>
      </div>
      <Separator />
      {_.map(instrItemThresholds, (iiThresholds: string[], ii) => (
        <div key={ii}>
          <Caption medium>
            {projectInstrItems.find(projectInstrItem => projectInstrItem._id === ii)?.name || ii}
          </Caption>
          <InputText value={iiThresholds.join(` - `)} disabled />
        </div>
      ))}
      <TextButton
        type="primary"
        onClick={() => {
          setLoadingAuditData(true);
          setShowingQualityLabModal(true);
        }}
        disabled={loadingAuditData || showingQualityLabModal || showingReviewerExclusionModal}
      >
        Update Thresholds
      </TextButton>
    </div>
  );
};

const formatPct = (top: number, bottom: number) => {
  return `${Math.floor((bottom ? top / bottom : 0) * 100)}%`;
};

const useStyles = makeStyles({
  nav: {
    flexShrink: 0,
    overflow: 'auto',
    transition: 'width 0.2s ease-in-out',
    width: 280,
    position: 'relative',
    padding: 16,
    borderRight: '1px solid var(--color-RemoGray01)',
    '& .nav-icon': {
      position: 'absolute',
      transition: 'transform 0.2s ease-in-out',
      top: 3,
      right: 12,
      cursor: 'pointer',
      padding: 10,
      color: 'var(--color-RemoGray75)',
    },
    '& .nav-body': {
      opacity: 1,
      transition: 'opacity 0.2s ease-in-out',
      color: 'var(--color-RemoGray75)',
    },
    '&.collapsed': {
      width: 48,
      '& .nav-icon': {
        transform: 'rotate(-180deg)',
      },
      '& .nav-body': {
        display: 'none',
      },
    },
  },
  title: {
    margin: 0,
    maxWidth: 200,
  },
  qualitySection: {
    '&.good': {
      color: 'var(--color-RemoGreen)',
    },
    '&.poor': {
      color: 'var(--color-RemoRed)',
    },
    '&.unknown': {
      color: 'var(--color-RemoGray50)',
    },
    fontSize: '1.5em',
    '& span': {
      fontWeight: 'bold',
    },
    '&.current': {
      color: 'var(--color-RemoGray50)',
    },
    '&.potential': {
      color: 'var(--color-RemoGreen)',
      '&.cantPass': {
        color: 'var(--color-RemoRed)',
      },
    },
    '&.desired': {
      color: 'var(--color-RemoGray75)',
    },
  },
});

export default React.memo(BenchmarkThresholdAuditSidebar);
